import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useEditorStore } from '~/shell/Store';
import { useMediaFetch } from '../hooks/useMediaFetch';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import SearchHeader from '../components/SearchHeader';
import MediaGrid from '../components/MediaGrid';
import { MediaFile } from '../components/MediaGrid';
import type { ImageGenerationContext } from '~/data/imageGenerationPolling';

const Container = styled.div`
  h3 {
    font-family: ralewayBold;
    font-weight: unset !important;
    font-size: 16px;
    line-height: 24px;
    text-align: left;
    text-underline-position: from-font;
    text-decoration-skip-ink: none;
    padding-bottom: 10px;
  }
`;

type AiSectionProps = {
  onSelect: (file: MediaFile) => void;
  files: MediaFile[];
  context?: ImageGenerationContext;
};

const AiSection: React.FC<AiSectionProps> = ({ onSelect, files, context }) => {
  const [search, setSearch] = useState('');
  const [style, setStyle] = useState('natural');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedFile, setGeneratedFile] = useState<MediaFile | null>(null);
  const { apiService } = useEditorStore();
  const { loading, fetchMedia, resetState, deleteMedia } = useMediaFetch(
    'image',
    'dalle'
  );
  const isRequestInProgress = useRef(false);

  const loadMore = async () => {
    if (isRequestInProgress.current) return;

    isRequestInProgress.current = true;
    try {
      const hasMore = await fetchMedia(page);
      setCanLoadMore(hasMore ?? false);
    } finally {
      isRequestInProgress.current = false;
    }
  };

  const {
    page,
    setPage,
    loadMoreRef,
    setCanLoadMore,
    isLoading: isInfiniteLoading
  } = useInfiniteScroll(loadMore);

  useEffect(() => {
    if (isRequestInProgress.current) return;

    isRequestInProgress.current = true;
    const init = async () => {
      try {
        resetState();
        setPage(1);
        setCanLoadMore(true);
        await loadMore();
      } finally {
        isRequestInProgress.current = false;
      }
    };

    init();
  }, []);

  const handleGenerate = async () => {
    if (!apiService || !search.trim() || isRequestInProgress.current) return;

    setIsGenerating(true);
    isRequestInProgress.current = true;
    try {
      const response = await apiService.generateImageWithAI(
        search,
        style,
        context
      );
      if (response && 'picture' in response && response.picture) {
        setGeneratedFile(response.picture as MediaFile);
      }
    } catch (error) {
      console.error('Error generating AI image:', error);
    } finally {
      setIsGenerating(false);
      isRequestInProgress.current = false;
    }
  };

  const handleDelete = async (id: number) => {
    try {
      const response = await deleteMedia(id);
      if (response.success) {
        if (generatedFile?.id === id) {
          setGeneratedFile(null);
        }
      }
    } catch (error) {
      console.error('Error deleting file:', error);
    }
  };

  const displayFiles =
    isGenerating || generatedFile
      ? generatedFile
        ? [generatedFile]
        : []
      : files;

  return (
    <Container>
      <h3>Generate image with AI</h3>
      <SearchHeader
        variant="ai"
        placeholder="Generate with AI"
        value={search}
        onChange={setSearch}
        onSearch={handleGenerate}
        loading={isGenerating}
        loadingText="Generating..."
        style={style}
        onStyleChange={setStyle}
      />
      {!isGenerating && !generatedFile && files && files.length > 0 && (
        <h3>Recently Generated</h3>
      )}
      {generatedFile && <h3>Generated Image</h3>}
      <MediaGrid
        files={displayFiles}
        loading={loading || isInfiniteLoading || isGenerating}
        type="image"
        onSelect={onSelect}
        onDelete={handleDelete}
        loadMoreRef={!isGenerating && !generatedFile ? loadMoreRef : undefined}
        showNoResults
      />
    </Container>
  );
};

export default AiSection;
